import type { ProviderType } from '../../api';
import { rosterFacilityCellText, rosterProviderCellText } from './roster.columns';
import type {
    RosterFacility,
    RosterListQuery,
    RosterPage,
    RosterProvider,
    RosterSort,
    RosterTypeCounts,
} from './roster.types';

/**
 * Search, filter, sort and paging over records held in memory — what the mock data source serves.
 * The real adapter does this server-side instead, so nothing outside a data source should
 * call these on a page it has already fetched.
 */

/** The fields the design searches, per tab. */
const PROVIDER_SEARCH_KEYS = ['name', 'npi', 'organization', 'type', 'address'];
const FACILITY_SEARCH_KEYS = ['name', 'npi', 'address'];

export function rosterProviderMatches(provider: RosterProvider, search: string): boolean {
    return matches(
        PROVIDER_SEARCH_KEYS.map(key => rosterProviderCellText(provider, key)),
        search,
    );
}

export function rosterFacilityMatches(facility: RosterFacility, search: string): boolean {
    return matches(
        FACILITY_SEARCH_KEYS.map(key => rosterFacilityCellText(facility, key)),
        search,
    );
}

function matches(values: string[], search: string): boolean {
    const needle = search.trim().toLowerCase();
    return !needle || values.join(' ').toLowerCase().includes(needle);
}

/**
 * Sort by a column's text, case-insensitively.
 *
 * Empty values go LAST in both directions, as in the design — flipping to descending should
 * not bring a page of blanks to the top. Ties break on `id`, so paging through equal
 * values never shows a record twice or skips one.
 */
export function rosterSort<T extends { id: string }>(
    records: T[],
    sort: RosterSort | undefined,
    textOf: (record: T, key: string) => string,
): T[] {
    if (!sort) {
        return records.slice();
    }
    const sign = sort.direction === 'asc' ? 1 : -1;
    return records.slice().sort((a, b) => {
        const av = textOf(a, sort.key).toLowerCase();
        const bv = textOf(b, sort.key).toLowerCase();
        if (av !== bv) {
            if (av === '') {
                return 1;
            }
            if (bv === '') {
                return -1;
            }
            return (av < bv ? -1 : 1) * sign;
        }
        return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
    });
}

export function rosterPaginate<T>(records: T[], offset: number, limit: number): RosterPage<T> {
    return { items: records.slice(offset, offset + limit), total: records.length };
}

export function rosterQueryProviders(providers: RosterProvider[], query: RosterListQuery): RosterPage<RosterProvider> {
    const filtered = providers.filter(
        provider =>
            (!query.providerType || provider.type === query.providerType) &&
            rosterProviderMatches(provider, query.search || ''),
    );
    return rosterPaginate(rosterSort(filtered, query.sort, rosterProviderCellText), query.offset, query.limit);
}

export function rosterQueryFacilities(
    facilities: RosterFacility[],
    query: RosterListQuery,
): RosterPage<RosterFacility> {
    const filtered = facilities.filter(facility => rosterFacilityMatches(facility, query.search || ''));
    return rosterPaginate(rosterSort(filtered, query.sort, rosterFacilityCellText), query.offset, query.limit);
}

export function rosterCountByType(providers: RosterProvider[]): RosterTypeCounts {
    const byType: Partial<Record<ProviderType, number>> = {};
    providers.forEach(provider => {
        byType[provider.type] = (byType[provider.type] || 0) + 1;
    });
    return { total: providers.length, byType };
}

/**
 * The "1–20 of 168" label. An empty result is "0 of 0", never "1–0 of 0".
 * `offset` is clamped by the caller's page, not here.
 */
export function rosterRangeLabel(offset: number, pageSize: number, total: number): string {
    if (total === 0) {
        return '0 of 0';
    }
    return `${offset + 1}–${Math.min(offset + pageSize, total)} of ${total}`;
}
